import { PiInfoFill, PiWarningFill } from 'react-icons/pi';
import { Box } from '../Box';
import { Text } from '../Text';
import type { PropsWithChildren } from 'react';
import { cn } from '@utils/cn';
import type { IconType } from 'react-icons';
import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

const alertVariants = cva(
  'rounded-10 border font-medium flex-row items-center gap-10',
  {
    variants: {
      variant: {
        info: 'bg-elz-neutral-3 border-elz-neutral-6 text-elz-neutral-11',
        warning: 'bg-elz-warning-3 border-elz-warning-6 text-elz-warning-11',
        danger: 'bg-elz-danger-3 border-elz-danger-6 text-elz-danger-11',
      },
      size: {
        sm: 'p-8',
        md: 'p-32 justify-center',
        lg: 'p-64 justify-center',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'sm',
    },
  },
);

type NonNullish<T> = T extends null | undefined ? never : T;
type AlertVariantProps = VariantProps<typeof alertVariants>;
type AlertProps = AlertVariantProps &
  PropsWithChildren<{
    className?: string;
    displayIcon?: boolean;
  }>;

type Variant = NonNullish<AlertVariantProps['variant']>;

const iconsMap: Record<Variant, IconType> = {
  info: PiInfoFill,
  warning: PiWarningFill,
  danger: PiWarningFill,
};

export const Alert: React.FC<AlertProps> = ({
  variant,
  size,
  children,
  className,
  displayIcon = true,
}) => {
  const Icon = iconsMap[variant || 'info'];

  return (
    <Box className={cn(alertVariants({ variant, size }), className)}>
      {displayIcon && <Icon className="size-18 shrink-0" />}
      {typeof children === 'string' ? (
        <Text variant="primary" className="text-inherit">
          {children}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
};
